import React, { useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import {
  Order,
  PartnerUser,
  Warehouse,
  AvailableCouriersResponse,
  WarehousesResponse,
  OrderAssignmentResponse,
} from '../../models/APIModels';
import Label from '../../components/Label';
import { axiosKK } from '../../services/networkRequest';
import Modal from 'react-modal';
import { Formik } from 'formik';
import errorMessage from '../../services/errorMessage';

interface OrderModalProps {
  update: (updated: Order) => void;
  close: () => void;
  editMode: boolean;
  order: Order;
}

interface AssignLogisticsValues {
  collectionCourierId: number | '';
  deliveryCourierId: number | '';
  warehouseId: number | '';
}

interface AssignLogisticsErrors {
  collectionCourierId?: string;
  deliveryCourierId?: string;
  warehouseId?: string;
}

function StatusDot({ assigned }: { assigned: boolean }) {
  return (
    <span
      className={`assign-status-dot ${assigned ? 'assign-status-dot--active' : 'assign-status-dot--empty'}`}
    />
  );
}

function getCourierDisplayName(courier: PartnerUser): string {
  if (courier.firstName || courier.lastName) {
    return [courier.firstName, courier.lastName].filter(Boolean).join(' ');
  }
  return courier.name || courier.email || `Courier #${courier.id}`;
}

function getCourierPhone(courier: PartnerUser): string | undefined {
  return courier.phoneNumber || courier.phone;
}

export default function OrderEditAssignLogisticsModal({
  editMode,
  order,
  close,
  update,
}: OrderModalProps): JSX.Element {
  const [couriers, setCouriers] = useState<PartnerUser[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [currentCollectionCourier, setCurrentCollectionCourier] = useState<PartnerUser | undefined>();
  const [currentDeliveryCourier, setCurrentDeliveryCourier] = useState<PartnerUser | undefined>();
  const [currentWarehouse, setCurrentWarehouse] = useState<Warehouse | undefined>();
  const [networkError, setNetworkError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<AssignLogisticsValues>({
    collectionCourierId: '',
    deliveryCourierId: '',
    warehouseId: '',
  });

  useEffect(() => {
    if (editMode) {
      setLoading(true);
      setNetworkError(undefined);

      Promise.all([
        axiosKK.get<AvailableCouriersResponse>('/admin/logistics/couriers'),
        axiosKK.get<WarehousesResponse>('/admin/logistics/warehouses'),
        axiosKK.get<OrderAssignmentResponse>(
          `/admin/logistics/orders/${order.id}/assignment`
        ),
      ])
        .then(([couriersRes, warehousesRes, assignmentRes]) => {
          setCouriers(couriersRes.data.couriers || []);
          setWarehouses(warehousesRes.data.warehouses || []);

          const assignment = assignmentRes.data;

          const collectionCourier = assignment.collectionCourier ?? assignment.courier;
          const deliveryCourier = assignment.deliveryCourier ?? assignment.courier;

          setCurrentCollectionCourier(collectionCourier);
          setCurrentDeliveryCourier(deliveryCourier);
          setCurrentWarehouse(assignment.warehouse);

          setInitialValues({
            collectionCourierId: collectionCourier?.id || '',
            deliveryCourierId: deliveryCourier?.id || '',
            warehouseId: assignment.warehouse?.id || '',
          });
        })
        .catch((error) => {
          errorMessage(setNetworkError)(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [editMode, order.id]);

  return (
    <Modal
      isOpen={editMode}
      onRequestClose={close}
      style={{
        content: {
          display: 'flex',
        },
      }}
      contentLabel="Assign Partner(s)"
    >
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validate={(values: AssignLogisticsValues) => {
          const errors: AssignLogisticsErrors = {};
          const hasAnyCourier = values.collectionCourierId || values.deliveryCourierId;
          if (!hasAnyCourier && !values.warehouseId) {
            errors.collectionCourierId = 'Select at least one courier or a warehouse';
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);
          setNetworkError(undefined);

          const payload: any = {
            collectionCourierId: values.collectionCourierId ? Number(values.collectionCourierId) : null,
            deliveryCourierId: values.deliveryCourierId ? Number(values.deliveryCourierId) : null,
            warehouseId: values.warehouseId ? Number(values.warehouseId) : null,
          };

          axiosKK
            .put<OrderAssignmentResponse>(
              `/admin/logistics/orders/${order.id}/assign`,
              payload
            )
            .then(({ data }) => {
              update(data.order);
              close();
            })
            .catch((error) => {
              errorMessage(setNetworkError)(error);
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          isSubmitting,
          handleChange,
          handleSubmit,
          errors,
          isValid,
          dirty,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="order-edit-form assign-logistics-form"
            name="assign-logistics-form"
          >
            <div className="modal-header">
              <div className="modal-header__titles">
                <h3>Assign Partner(s)</h3>
                <span className="modal-header__subtitle">
                  Order #{order.id} &middot; {order.city || 'Unknown city'}
                </span>
              </div>
              <SubmitButton red={true} onClick={close}>
                Close
              </SubmitButton>
            </div>

            <div className="modal-content">
              {loading ? (
                <div className="assign-loading">
                  <span>Loading available partners...</span>
                </div>
              ) : (
                <>
                  <div className="assign-status-summary">
                    <div className="assign-status-row">
                      <StatusDot assigned={!!currentCollectionCourier} />
                      <span className="assign-status-label">Collection Courier</span>
                      <span className="assign-status-value">
                        {currentCollectionCourier
                          ? getCourierDisplayName(currentCollectionCourier)
                          : 'Not assigned'}
                      </span>
                    </div>
                    <div className="assign-status-row">
                      <StatusDot assigned={!!currentDeliveryCourier} />
                      <span className="assign-status-label">Delivery Courier</span>
                      <span className="assign-status-value">
                        {currentDeliveryCourier
                          ? getCourierDisplayName(currentDeliveryCourier)
                          : 'Not assigned'}
                      </span>
                    </div>
                    <div className="assign-status-row">
                      <StatusDot assigned={!!currentWarehouse} />
                      <span className="assign-status-label">Warehouse</span>
                      <span className="assign-status-value">
                        {currentWarehouse
                          ? `${currentWarehouse.name}${currentWarehouse.city ? ` — ${currentWarehouse.city}` : ''}`
                          : 'Not assigned'}
                      </span>
                    </div>
                  </div>

                  {errors.collectionCourierId && (
                    <span className="error">{errors.collectionCourierId}</span>
                  )}

                  <div className="assign-section">
                    <h5 className="assign-section-title">Couriers</h5>

                    <div className="order-edit-inputs">
                      <Label htmlFor="collectionCourierId">Collection Courier</Label>
                      <select
                        className="form-select"
                        id="collectionCourierId"
                        name="collectionCourierId"
                        value={values.collectionCourierId}
                        onChange={handleChange}
                      >
                        <option value="">— Not assigned —</option>
                        {couriers.map((courier) => {
                          const displayName = getCourierDisplayName(courier);
                          const phone = getCourierPhone(courier);
                          return (
                            <option key={courier.id} value={courier.id}>
                              {displayName}
                              {phone ? ` (${phone})` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div className="order-edit-inputs">
                      <Label htmlFor="deliveryCourierId">Delivery Courier</Label>
                      <select
                        className="form-select"
                        id="deliveryCourierId"
                        name="deliveryCourierId"
                        value={values.deliveryCourierId}
                        onChange={handleChange}
                      >
                        <option value="">— Not assigned —</option>
                        {couriers.map((courier) => {
                          const displayName = getCourierDisplayName(courier);
                          const phone = getCourierPhone(courier);
                          return (
                            <option key={courier.id} value={courier.id}>
                              {displayName}
                              {phone ? ` (${phone})` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div className="assign-section">
                    <h5 className="assign-section-title">Warehouse</h5>

                    <div className="order-edit-inputs">
                      <Label htmlFor="warehouseId">Storage Warehouse</Label>
                      <select
                        className="form-select"
                        id="warehouseId"
                        name="warehouseId"
                        value={values.warehouseId}
                        onChange={handleChange}
                      >
                        <option value="">— Not assigned —</option>
                        {warehouses.map((warehouse) => {
                          const displayName = warehouse.name || `Warehouse #${warehouse.id}`;
                          const location = [warehouse.city, warehouse.postcode].filter(Boolean).join(', ');
                          return (
                            <option key={warehouse.id} value={warehouse.id}>
                              {displayName}{location ? ` - ${location}` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
            {networkError && <span className="error">{networkError}</span>}
            <SubmitButton
              disabled={loading || !isValid || !dirty}
              isSubmitting={isSubmitting}
            >
              Save Assignments
            </SubmitButton>
          </form>
        )}
      </Formik>
    </Modal>
  );
}
